// AdminDataUpdates.jsx - Admin section for data updates
import React, { useState, useEffect, useCallback } from "react";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { authService } from "../../services/auth";
import logger from '../../utils/logger';

function AdminDataUpdates({ _user }) {
  const [activeTab, setActiveTab] = useState('bgg-update');
  const [uploading, setUploading] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [activeTasks, setActiveTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // success or error
  const [selectedFile, setSelectedFile] = useState(null);
  const [similarityStats, setSimilarityStats] = useState(null);
  const [venueStats, setVenueStats] = useState(null);
  const [pendingDeactivations, setPendingDeactivations] = useState([]);
  const [selectedPendingIds, setSelectedPendingIds] = useState(new Set());
  const [unenrichedEntries, setUnenrichedEntries] = useState([]);
  const [unenrichedLoading, setUnenrichedLoading] = useState(false);
  const [unenrichedVenueFilter, setUnenrichedVenueFilter] = useState('all');
  const [unenrichedSearch, setUnenrichedSearch] = useState('');
  const [unenrichedPagination, setUnenrichedPagination] = useState({ limit: 50, offset: 0, total: 0 });
  const [editingUnenriched, setEditingUnenriched] = useState({});
  const [savingUnenrichedIds, setSavingUnenrichedIds] = useState(new Set());

  const showMessage = useCallback((text, type) => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
      setMessageType("");
    }, 3000);
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/admin/data-updates/stats`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      logger.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchSimilarityStats = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/admin/data-updates/similarity-engine-status`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setSimilarityStats(data);
      }
    } catch (error) {
      logger.error('Failed to fetch similarity engine stats:', error);
    }
  }, []);

  const fetchVenueStats = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/admin/data-updates/venues-stats`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setVenueStats(data);
      }
    } catch (error) {
      logger.error('Failed to fetch venue stats:', error);
    }
  }, []);

  const fetchPendingDeactivations = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/admin/data-updates/venues-reconcile-enriched/pending`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setPendingDeactivations(data.pending || []);
      }
    } catch (error) {
      logger.error('Failed to fetch pending deactivations:', error);
    }
  }, []);

  const fetchUnenrichedEntries = useCallback(async (override = {}) => {
    setUnenrichedLoading(true);
    try {
      const venueCode = override.venue_code !== undefined ? override.venue_code : unenrichedVenueFilter;
      const search = override.search !== undefined ? override.search : unenrichedSearch;
      const offset = override.offset !== undefined ? override.offset : unenrichedPagination.offset;
      const limit = unenrichedPagination.limit;

      const params = new URLSearchParams();
      params.append('limit', String(limit));
      params.append('offset', String(offset));
      if (venueCode && venueCode !== 'all') params.append('venue_code', venueCode);
      if (search && search.trim()) params.append('search', search.trim());

      const res = await httpRequest(`${API_BASE}/admin/data-updates/venues-unenriched?${params.toString()}`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setUnenrichedEntries(data.items || []);
        setUnenrichedPagination((prev) => ({
          ...prev,
          total: data.total || 0,
          offset: data.offset || 0,
        }));
        // Pre-populate editable state from existing URLs
        const initialEditing = {};
        (data.items || []).forEach((item) => {
          const tier = item.metadata?.tier || item.metadata?.access_type || 'free';
          initialEditing[item.venue_game_id] = {
            url: item.venue_url || '',
            tier: tier === 'premium' ? 'premium' : 'free',
          };
        });
        setEditingUnenriched(initialEditing);
      }
    } catch (error) {
      logger.error('Failed to fetch unenriched entries:', error);
    } finally {
      setUnenrichedLoading(false);
    }
  }, [unenrichedVenueFilter, unenrichedSearch, unenrichedPagination.limit, unenrichedPagination.offset]);

  const fetchCompletedTasks = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/admin/data-updates/completed-tasks`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setCompletedTasks(data);
      }
    } catch (error) {
      logger.error('Failed to fetch completed tasks:', error);
    }
  }, []);

  const fetchActiveTasks = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/admin/data-updates/active-tasks`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setActiveTasks(data);

        // Update current task if it's still active
        if (currentTask) {
          const updated = data.find(t => t.task_id === currentTask.task_id);
          if (updated) {
            setCurrentTask(updated);
          } else if (updated === undefined) {
            // Task completed, fetch completed tasks
            fetchCompletedTasks();
            setCurrentTask(null);
          }
        }
      }
    } catch (error) {
      logger.error('Failed to fetch active tasks:', error);
    }
  }, [currentTask, fetchCompletedTasks]);

  // Poll for updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTasks.length > 0) {
        fetchActiveTasks();
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(interval);
  }, [activeTasks.length, fetchActiveTasks]);

  useEffect(() => {
    fetchStats();
    fetchCompletedTasks();
    fetchSimilarityStats();
    fetchVenueStats();
    fetchPendingDeactivations();
  }, [fetchStats, fetchCompletedTasks, fetchSimilarityStats, fetchVenueStats, fetchPendingDeactivations]);

  useEffect(() => {
    if (activeTab === 'venues-sync') {
      fetchUnenrichedEntries();
    }
  }, [activeTab, fetchUnenrichedEntries]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['text/csv', 'application/zip', 'application/x-zip-compressed'];
      if (!validTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.csv') && !file.name.toLowerCase().endsWith('.zip')) {
        showMessage('Please select a CSV or ZIP file', 'error');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleVenuesSync = async () => {
    setUploading(true);

    try {
      const res = await httpRequest(`${API_BASE}/admin/data-updates/trigger-jobs`, {
        method: "POST",
        headers: authService.getAuthHeaders(),
      });

      if (res.ok) {
        const data = await res.json();
        showMessage(data.detail || data.status || 'pista-jobs service triggered', 'success');
        fetchStats();
        fetchCompletedTasks();
      } else {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to trigger pista-jobs');
      }
    } catch (error) {
      logger.error('Error triggering pista-jobs:', error);
      showMessage(`Failed to trigger pista-jobs: ${error.message}`, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadReconciliationReport = async () => {
    try {
      const res = await httpRequest(`${API_BASE}/admin/data-updates/venues-reconciliation-report`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });

      if (res.ok) {
        // Get filename from Content-Disposition header
        const contentDisposition = res.headers.get('Content-Disposition');
        let filename = 'venues_reconciliation_report.csv';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        // Create blob and download
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        showMessage('Reconciliation report downloaded successfully!', 'success');
      } else {
        showMessage('Failed to download reconciliation report', 'error');
      }
    } catch (error) {
      logger.error('Error downloading reconciliation report:', error);
      showMessage('Failed to download reconciliation report', 'error');
    }
  };

  const handleReconcileEnriched = async () => {
    setUploading(true);

    try {
      const res = await httpRequest(`${API_BASE}/admin/data-updates/venues-reconcile-enriched`, {
        method: "POST",
        headers: authService.getAuthHeaders(),
      });

      if (res.ok) {
        const data = await res.json();
        const { task_id } = data;
        setCurrentTask({
          task_id,
          task_type: 'venues',
          status: 'processing_started',
          progress: 0,
          message: 'Enriched venue reconciliation started...',
        });

        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await httpRequest(`${API_BASE}/admin/data-updates/status/${task_id}`, {
              method: "GET",
              headers: authService.getAuthHeaders(),
            });
            if (statusRes.ok) {
              const taskStatus = await statusRes.json();
              setCurrentTask(taskStatus);

              if (taskStatus.status === 'completed' || taskStatus.status === 'failed') {
                clearInterval(pollInterval);
                fetchStats();
                fetchCompletedTasks();
                fetchVenueStats();
                fetchPendingDeactivations();

                if (taskStatus.status === 'completed') {
                  showMessage('Enriched venue reconciliation completed successfully!', 'success');
                } else {
                  showMessage(`Enriched venue reconciliation failed: ${taskStatus.error}`, 'error');
                }
              }
            } else {
              logger.warn(`Polling returned status ${statusRes.status}, continuing to poll`);
            }
          } catch (error) {
            logger.error('Error polling task status:', error);
            if (error.message && error.message.includes('Unauthorized')) {
              clearInterval(pollInterval);
              showMessage('Authentication failed. Please refresh and try again.', 'error');
            }
          }
        }, 2000);
      }
    } catch (error) {
      logger.error('Error starting enriched venue reconciliation:', error);
      showMessage('Failed to start enriched venue reconciliation', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handlePendingSelectionChange = (venue_game_id) => {
    const next = new Set(selectedPendingIds);
    if (next.has(venue_game_id)) {
      next.delete(venue_game_id);
    } else {
      next.add(venue_game_id);
    }
    setSelectedPendingIds(next);
  };

  const handleSelectAllPending = (checked) => {
    if (checked) {
      setSelectedPendingIds(new Set(pendingDeactivations.map((p) => p.venue_game_id)));
    } else {
      setSelectedPendingIds(new Set());
    }
  };

  const handleApprovePending = async () => {
    if (selectedPendingIds.size === 0) return;
    const ids = Array.from(selectedPendingIds);
    try {
      const res = await httpRequest(`${API_BASE}/admin/data-updates/venues-reconcile-enriched/approve`, {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ venue_game_ids: ids }),
      });
      if (res.ok) {
        showMessage(`Approved ${ids.length} deactivation(s)`, 'success');
        setSelectedPendingIds(new Set());
        fetchPendingDeactivations();
        fetchVenueStats();
        fetchUnenrichedEntries({ offset: 0 });
      } else {
        showMessage('Failed to approve deactivations', 'error');
      }
    } catch (error) {
      logger.error('Error approving deactivations:', error);
      showMessage('Failed to approve deactivations', 'error');
    }
  };

  const handleRejectPending = async () => {
    if (selectedPendingIds.size === 0) return;
    const ids = Array.from(selectedPendingIds);
    try {
      const res = await httpRequest(`${API_BASE}/admin/data-updates/venues-reconcile-enriched/reject`, {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ venue_game_ids: ids }),
      });
      if (res.ok) {
        showMessage(`Rejected ${ids.length} deactivation proposal(s)`, 'success');
        setSelectedPendingIds(new Set());
        fetchPendingDeactivations();
      } else {
        showMessage('Failed to reject deactivations', 'error');
      }
    } catch (error) {
      logger.error('Error rejecting deactivations:', error);
      showMessage('Failed to reject deactivations', 'error');
    }
  };

  const handleUnenrichedEditChange = (venue_game_id, field, value) => {
    setEditingUnenriched((prev) => ({
      ...prev,
      [venue_game_id]: {
        ...(prev[venue_game_id] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveUnenriched = async (venue_game_id) => {
    const edit = editingUnenriched[venue_game_id];
    if (!edit || !edit.url || !edit.tier) {
      showMessage('Please provide a URL and price tier', 'error');
      return;
    }

    setSavingUnenrichedIds((prev) => new Set(prev).add(venue_game_id));
    try {
      const res = await httpRequest(`${API_BASE}/admin/data-updates/venues-enrich-manual`, {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          venue_game_id,
          venue_url: edit.url,
          price_tier: edit.tier,
        }),
      });

      if (res.ok) {
        showMessage('Entry enriched successfully', 'success');
        fetchUnenrichedEntries();
        fetchVenueStats();
      } else {
        const data = await res.json().catch(() => ({}));
        showMessage(data.detail || 'Failed to enrich entry', 'error');
      }
    } catch (error) {
      logger.error('Error enriching entry:', error);
      showMessage('Failed to enrich entry', 'error');
    } finally {
      setSavingUnenrichedIds((prev) => {
        const next = new Set(prev);
        next.delete(venue_game_id);
        return next;
      });
    }
  };

  const handleUnenrichedPageChange = (newOffset) => {
    if (newOffset < 0) return;
    if (newOffset >= unenrichedPagination.total) return;
    setUnenrichedPagination((prev) => ({ ...prev, offset: newOffset }));
    fetchUnenrichedEntries({ offset: newOffset });
  };

  const handleUnenrichedFilterChange = (value) => {
    setUnenrichedVenueFilter(value);
    setUnenrichedPagination((prev) => ({ ...prev, offset: 0 }));
    fetchUnenrichedEntries({ venue_code: value, offset: 0 });
  };

  const handleUnenrichedSearchChange = (value) => {
    setUnenrichedSearch(value);
    setUnenrichedPagination((prev) => ({ ...prev, offset: 0 }));
    fetchUnenrichedEntries({ search: value, offset: 0 });
  };

  const formatReason = (reason) => {
    switch (reason) {
      case 'removed_from_bgg_di': return 'Removed from BGG Digital Implementations';
      case 'removed_by_admin': return 'Removed by admin (Digital Implementations)';
      case 'not_found_on_venue': return 'Not found on venue';
      case 'game_not_in_bgg': return 'Game no longer in BGG';
      default: return reason;
    }
  };

  const handleSimilarityEngineUpdate = async () => {
    setUploading(true);

    try {
      const res = await httpRequest(`${API_BASE}/admin/data-updates/trigger-jobs`, {
        method: "POST",
        headers: authService.getAuthHeaders(),
      });

      if (res.ok) {
        const data = await res.json();
        showMessage(data.detail || data.status || 'pista-jobs service triggered', 'success');
        fetchStats();
        fetchCompletedTasks();
        fetchSimilarityStats();
      } else {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to trigger pista-jobs');
      }
    } catch (error) {
      logger.error('Similarity engine update failed:', error);
      showMessage(`Similarity engine update failed: ${error.message}`, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showMessage('Please select a file first', 'error');
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const res = await httpRequest(`${API_BASE}/admin/data-updates/bgg-upload`, {
        method: "POST",
        headers: authService.getAuthHeaders(),
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        const { task_id } = data;
        setCurrentTask({
          task_id,
          status: 'processing_started',
          progress: 0,
          message: 'Processing started...',
        });

        // Start polling for this task
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await httpRequest(`${API_BASE}/admin/data-updates/status/${task_id}`, {
              method: "GET",
              headers: authService.getAuthHeaders(),
            });
            if (statusRes.ok) {
              const taskStatus = await statusRes.json();
              setCurrentTask(taskStatus);

              if (taskStatus.status === 'completed' || taskStatus.status === 'failed') {
                clearInterval(pollInterval);
                fetchStats();
                fetchCompletedTasks();

                if (taskStatus.status === 'completed') {
                  showMessage('BGG update completed successfully!', 'success');
                } else {
                  showMessage(`BGG update failed: ${taskStatus.error}`, 'error');
                }
              }
            }
          } catch (error) {
            logger.error('Error polling task status:', error);
            clearInterval(pollInterval);
          }
        }, 1000);
      } else {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

    } catch (error) {
      logger.error('Upload failed:', error);
      showMessage(`Upload failed: ${error.message}`, 'error');
    } finally {
      setUploading(false);
      setSelectedFile(null);
    }
  };

  const deleteTask = async (taskId) => {
    try {
      const res = await httpRequest(`${API_BASE}/admin/data-updates/task/${taskId}`, {
        method: "DELETE",
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        showMessage('Task deleted', 'success');
        fetchCompletedTasks();
      }
    } catch (error) {
      logger.error('Failed to delete task:', error);
      showMessage('Failed to delete task', 'error');
    }
  };

  const stopTask = async (taskId) => {
    try {
      const res = await httpRequest(`${API_BASE}/admin/data-updates/stop/${taskId}`, {
        method: "POST",
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        showMessage('Stop signal sent to task', 'success');
        // Update current task status immediately
        if (currentTask && currentTask.task_id === taskId) {
          setCurrentTask({
            ...currentTask,
            status: 'stopping',
            message: 'Stopping task...'
          });
        }
      }
    } catch (error) {
      logger.error('Failed to stop task:', error);
      showMessage('Failed to stop task', 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#52c41a';
      case 'failed': return '#ff4d4f';
      case 'processing': return '#1890ff';
      case 'uploading': return '#fa8c16';
      case 'stopping': return '#fa8c16';
      case 'stopped': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };


  return (
    <div className="admin-section">
      <h2>Data Updates</h2>

      {message && (
        <div className={`admin-message ${messageType}`}>
          {message}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="admin-controls">
        <button
          className={`admin-tab-btn ${activeTab === 'bgg-update' ? 'active' : ''}`}
          onClick={() => setActiveTab('bgg-update')}
        >
          Update from BGG
        </button>
        <button
          className={`admin-tab-btn ${activeTab === 'venues-sync' ? 'active' : ''}`}
          onClick={() => setActiveTab('venues-sync')}
        >
          Online Venues Sync
        </button>
        <button
          className={`admin-tab-btn ${activeTab === 'similarity-engine' ? 'active' : ''}`}
          onClick={() => setActiveTab('similarity-engine')}
        >
          Similarity Engine
        </button>
        <button
          className={`admin-tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>

      {/* BGG Update Tab */}
      {activeTab === 'bgg-update' && (
        <div className="admin-tab-content">
          {/* File Upload Section */}
          <div className="admin-upload-section">
            <h3>Upload BGG Data File</h3>
            <p className="admin-description">
              Upload a CSV file from BGG or a ZIP file containing the CSV.
              The file should contain game IDs and other BGG data.
            </p>

            <div className="admin-file-upload">
              <input
                type="file"
                id="bgg-file-input"
                accept=".csv,.zip"
                onChange={handleFileSelect}
                disabled={uploading}
                style={{ display: 'none' }}
              />
              <label
                htmlFor="bgg-file-input"
                className={`admin-upload-btn ${uploading ? 'disabled' : ''}`}
              >
                {uploading ? 'Processing...' : 'Choose File'}
              </label>

              {selectedFile && (
                <div className="admin-selected-file">
                  <span className="file-name">{selectedFile.name}</span>
                  <span className="file-size">({formatFileSize(selectedFile.size)})</span>
                  <button
                    className="admin-remove-file"
                    onClick={() => setSelectedFile(null)}
                    disabled={uploading}
                  >
                    ✕
                  </button>
                </div>
              )}

              <button
                className="admin-btn admin-btn-primary"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
              >
                {uploading ? 'Uploading...' : 'Upload and Process'}
              </button>
            </div>
          </div>

          {/* Current Task Progress */}
          {currentTask && currentTask.task_type === 'bgg' && (
            <div className="admin-progress-section">
              <h3>Current Update Progress</h3>
              <div className="admin-task-info">
                <div className="task-detail">
                  <strong>Task ID:</strong> <code>{currentTask.task_id.substring(0, 8)}...</code>
                </div>
                <div className="task-detail">
                  <strong>Status:</strong>
                  <span
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(currentTask.status) }}
                  >
                    {currentTask.status?.toUpperCase()}
                  </span>
                </div>
                <div className="task-detail">
                  <strong>Progress:</strong>
                  <div className="progress-container">
                    <div
                      className="progress-bar"
                      style={{
                        width: `${currentTask.progress || 0}%`,
                        backgroundColor: currentTask.status === 'failed' ? '#ff4d4f' : '#1890ff'
                      }}
                    />
                    <span className="progress-text">{Math.round(currentTask.progress || 0)}%</span>
                  </div>
                </div>
                <div className="task-detail">
                  <strong>Status:</strong> <span className="task-message">{currentTask.message}</span>
                </div>
              </div>

              {currentTask.summary && (
                <div className="admin-summary success">
                  <h4>✓ {currentTask.task_type === 'venues' ? 'Venues Sync' : currentTask.task_type === 'venues_reconcile' ? 'Enriched Venue Reconciliation' : 'Update'} Completed</h4>
                  <div className="summary-stats">
                    {currentTask.task_type === 'venues' ? (
                      <>
                        <div>Total Venues: {currentTask.summary.total_venues}</div>
                        <div>Active: {currentTask.summary.active_venues}</div>
                        <div>Enriched: {currentTask.summary.enriched_venues}</div>
                        <div>Success Rate: {currentTask.summary.success_rate.toFixed(1)}%</div>
                        {currentTask.summary.venues_by_code && (
                          <div className="venue-breakdown">
                            <strong>By Platform:</strong>
                            {Object.entries(currentTask.summary.venues_by_code).map(([code, stats]) => (
                              <div key={code} className="venue-stats">
                                <span className="venue-code">{code.toUpperCase()}:</span>
                                <span>Total: {stats.total}, Active: {stats.active}, Enriched: {stats.enriched}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : currentTask.task_type === 'venues_reconcile' ? (
                      <>
                        <div>Processed: {currentTask.summary.processed}</div>
                        <div>Auto-Updated: {currentTask.summary.auto_updated}</div>
                        <div>Pending Deactivations: {currentTask.summary.pending_deactivations}</div>
                      </>
                    ) : (
                      <>
                        <div>Total Games: {currentTask.summary.total_games}</div>
                        <div>Successful: {currentTask.summary.successful}</div>
                        <div>Failed: {currentTask.summary.failed}</div>
                        <div>Success Rate: {currentTask.summary.success_rate.toFixed(1)}%</div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {currentTask.error && (
                <div className="admin-summary error">
                  <h4>✗ {currentTask.status === 'stopped' ? 'Update Stopped' : 'Update Failed'}</h4>
                  <div className="error-message">{currentTask.error}</div>
                </div>
              )}

              {currentTask.status === 'processing' && (
                <div className="admin-controls" style={{ marginTop: '15px' }}>
                  <button
                    className="admin-btn admin-btn-danger"
                    onClick={() => stopTask(currentTask.task_id)}
                    disabled={currentTask.status === 'stopping'}
                  >
                    {currentTask.status === 'stopping' ? 'Stopping...' : '⏹ Stop Update'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Active Tasks */}
          {activeTasks.filter(task => task.task_type === 'bgg').length > 0 && (
            <div className="admin-active-tasks">
              <h3>Active Tasks</h3>
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Task ID</th>
                      <th>File Name</th>
                      <th>Status</th>
                      <th>Progress</th>
                      <th>Started</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeTasks.filter(task => task.task_type === 'bgg').map((task) => (
                      <tr key={task.task_id}>
                        <td><code>{task.task_id.substring(0, 8)}...</code></td>
                        <td>{task.filename || 'N/A'}</td>
                        <td>
                          <span
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(task.status) }}
                          >
                            {task.status?.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <div className="progress-container">
                            <div
                              className="progress-bar small"
                              style={{
                                width: `${task.progress || 0}%`,
                                backgroundColor: task.status === 'failed' ? '#ff4d4f' : '#1890ff'
                              }}
                            />
                            <span className="progress-text">{Math.round(task.progress || 0)}%</span>
                          </div>
                          <div className="task-message-small">{task.message}</div>
                        </td>
                        <td>{formatDate(task.started_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Statistics */}
          {stats && (
            <div className="admin-stats">
              <h3>Update Statistics</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-value">{stats.active_tasks}</div>
                  <div className="stat-label">Active Tasks</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{stats.total_completed}</div>
                  <div className="stat-label">Total Completed</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{stats.last_7_days.completed}</div>
                  <div className="stat-label">Last 7 Days</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">
                    {stats.last_7_days.completed > 0
                      ? `${(stats.last_7_days.successful / stats.last_7_days.completed * 100).toFixed(1)}%`
                      : 'N/A'
                    }
                  </div>
                  <div className="stat-label">Success Rate (7d)</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Online Venues Sync Tab */}
      {activeTab === 'venues-sync' && (
        <div className="admin-tab-content">
          {/* Venues Sync Section */}
          <div className="admin-upload-section">
            <h3>Online Venues Synchronization</h3>
            <p className="admin-description">
              Synchronize online venues cache with latest data and review entries that may need to be deactivated.
            </p>

            {/* Venue Breakdown */}
            {venueStats && venueStats.venues_by_code && (
              <div className="admin-venue-breakdown" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                <h4>Breakdown by Venue</h4>
                {Object.entries(venueStats.venues_by_code).map(([venueCode, stats]) => (
                  <div key={venueCode} className="admin-venue-stat-row" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    backgroundColor: 'var(--bg-secondary, #f5f5f5)',
                    borderRadius: '8px',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{ fontWeight: '600' }}>{venueCode.toUpperCase()}</div>
                    <div style={{ display: 'flex', gap: '1.5rem' }}>
                      <span>Total: {stats.total}</span>
                      <span>Active: {stats.active}</span>
                      <span>Enriched: {stats.enriched}</span>
                      <span>Unenriched: {stats.unenriched}</span>
                      <span>Rate: {stats.enrichment_rate.toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="admin-file-upload" style={{ marginTop: '1rem' }}>
              <button
                className="admin-btn admin-btn-primary"
                onClick={handleVenuesSync}
                disabled={uploading}
              >
                {uploading ? 'Syncing...' : 'Sync Venues'}
              </button>
              <button
                className="admin-btn admin-btn-secondary"
                onClick={handleReconcileEnriched}
                disabled={uploading}
                style={{ marginLeft: '10px' }}
              >
                Run Reconciliation
              </button>
            </div>
          </div>

          {/* Unenriched Entries Section */}
          <div className="admin-match-review-section" style={{ marginTop: '2rem' }}>
            <h3>Unenriched Games ({unenrichedPagination.total})</h3>
            <p className="admin-description">
              Games that were not automatically matched by the venue sync. Edit the URL and price tier, then save to enrich the entry manually.
            </p>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <select
                value={unenrichedVenueFilter}
                onChange={(e) => handleUnenrichedFilterChange(e.target.value)}
                disabled={unenrichedLoading}
              >
                <option value="all">All venues</option>
                <option value="bga">BGA</option>
                <option value="yucata">Yucata</option>
              </select>
              <input
                type="text"
                placeholder="Search game name..."
                value={unenrichedSearch}
                onChange={(e) => handleUnenrichedSearchChange(e.target.value)}
                disabled={unenrichedLoading}
                style={{ minWidth: '220px' }}
              />
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => fetchUnenrichedEntries({ offset: 0 })}
                disabled={unenrichedLoading}
              >
                Refresh
              </button>
            </div>

            {unenrichedLoading ? (
              <div className="admin-empty-state">Loading unenriched games...</div>
            ) : unenrichedEntries.length === 0 ? (
              <div className="admin-empty-state">No unenriched games found.</div>
            ) : (
              <>
                <div className="matches-table-container" style={{ marginBottom: '1rem' }}>
                  <table className="matches-table">
                    <thead>
                      <tr>
                        <th>Venue</th>
                        <th>Game Name</th>
                        <th>Normalized Name</th>
                        <th style={{ minWidth: '280px' }}>Venue URL</th>
                        <th>Price Tier</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unenrichedEntries.map((entry) => {
                        const edit = editingUnenriched[entry.venue_game_id] || { url: entry.venue_url || '', tier: 'free' };
                        return (
                          <tr key={entry.venue_game_id}>
                            <td>{entry.venue_code?.toUpperCase()}</td>
                            <td>{entry.game_name || entry.venue_game_id}</td>
                            <td>{entry.normalized_name || '-'}</td>
                            <td>
                              <input
                                type="text"
                                value={edit.url}
                                onChange={(e) => handleUnenrichedEditChange(entry.venue_game_id, 'url', e.target.value)}
                                placeholder="https://..."
                                style={{ width: '100%' }}
                              />
                            </td>
                            <td>
                              <select
                                value={edit.tier}
                                onChange={(e) => handleUnenrichedEditChange(entry.venue_game_id, 'tier', e.target.value)}
                              >
                                <option value="free">Free</option>
                                <option value="premium">Premium</option>
                              </select>
                            </td>
                            <td>
                              <button
                                className="admin-btn admin-btn-primary"
                                onClick={() => handleSaveUnenriched(entry.venue_game_id)}
                                disabled={savingUnenrichedIds.has(entry.venue_game_id)}
                              >
                                {savingUnenrichedIds.has(entry.venue_game_id) ? 'Saving...' : 'Save'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    Showing {Math.min(unenrichedPagination.offset + 1, unenrichedPagination.total)}-
                    {Math.min(unenrichedPagination.offset + unenrichedEntries.length, unenrichedPagination.total)} of {unenrichedPagination.total}
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      className="admin-btn admin-btn-secondary"
                      onClick={() => handleUnenrichedPageChange(unenrichedPagination.offset - unenrichedPagination.limit)}
                      disabled={unenrichedPagination.offset === 0}
                    >
                      Previous
                    </button>
                    <button
                      className="admin-btn admin-btn-secondary"
                      onClick={() => handleUnenrichedPageChange(unenrichedPagination.offset + unenrichedPagination.limit)}
                      disabled={unenrichedPagination.offset + unenrichedPagination.limit >= unenrichedPagination.total}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Deactivation Report Section */}
          <div className="admin-match-review-section" style={{ marginTop: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Deactivation Report</h3>
              <button
                className="admin-btn admin-btn-secondary"
                onClick={handleDownloadReconciliationReport}
                disabled={uploading}
              >
                Download Report
              </button>
            </div>
            <p className="admin-description">
              Entries proposed for deactivation based on removed BGG Digital Implementations tags or absence from the live venue. Run <strong>Run Reconciliation</strong> to refresh this report.
            </p>

            {pendingDeactivations.length === 0 ? (
              <div className="admin-empty-state">No pending deactivations. Run reconciliation to generate the latest report.</div>
            ) : (
              <>
                <div className="matches-table-container" style={{ marginBottom: '1rem' }}>
                  <table className="matches-table">
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            onChange={(e) => handleSelectAllPending(e.target.checked)}
                            checked={selectedPendingIds.size === pendingDeactivations.length && pendingDeactivations.length > 0}
                          />
                        </th>
                        <th>Venue</th>
                        <th>Game Name</th>
                        <th>URL</th>
                        <th>Access</th>
                        <th>Reason</th>
                        <th>Proposed At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingDeactivations.map((entry) => (
                        <tr key={entry.venue_game_id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedPendingIds.has(entry.venue_game_id)}
                              onChange={() => handlePendingSelectionChange(entry.venue_game_id)}
                            />
                          </td>
                          <td>{entry.venue_code?.toUpperCase()}</td>
                          <td>{entry.game_name || entry.venue_game_id}</td>
                          <td style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {entry.url ? (
                              <a href={entry.url} target="_blank" rel="noopener noreferrer" title={entry.url}>
                                {entry.url}
                              </a>
                            ) : '-'}
                          </td>
                          <td>
                            {entry.access_type ? (
                              `${entry.access_type}${entry.tier && entry.tier !== entry.access_type ? ` (${entry.tier})` : ''}${entry.premium !== undefined && entry.premium !== null ? ` — ${entry.premium ? 'Premium' : 'Free'}` : ''}`
                            ) : '-'}
                          </td>
                          <td>{formatReason(entry.reason)}</td>
                          <td>{entry.proposed_at ? new Date(entry.proposed_at).toLocaleString() : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="match-actions" style={{ display: 'flex', gap: '10px' }}>
                  <button
                    className="admin-btn admin-btn-danger"
                    onClick={handleApprovePending}
                    disabled={selectedPendingIds.size === 0}
                  >
                    Approve Deactivation{selectedPendingIds.size > 0 ? ` (${selectedPendingIds.size})` : ''}
                  </button>
                  <button
                    className="admin-btn admin-btn-secondary"
                    onClick={handleRejectPending}
                    disabled={selectedPendingIds.size === 0}
                  >
                    Reject Proposal{selectedPendingIds.size > 0 ? ` (${selectedPendingIds.size})` : ''}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Current Task Progress */}
          {currentTask && (currentTask.task_type === 'venues' || currentTask.task_type === 'venues_reconcile') && (
            <div className="admin-progress-section">
              <h3>{currentTask.task_type === 'venues_reconcile' ? 'Enriched Venue Reconciliation Progress' : 'Venues Sync Progress'}</h3>
              <div className="admin-task-info">
                <div className="task-detail">
                  <strong>Task ID:</strong> <code>{currentTask.task_id.substring(0, 8)}...</code>
                </div>
                <div className="task-detail">
                  <strong>Status:</strong>
                  <span
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(currentTask.status) }}
                  >
                    {currentTask.status?.toUpperCase()}
                  </span>
                </div>
                <div className="task-detail">
                  <strong>Progress:</strong>
                  <div className="progress-container">
                    <div
                      className="progress-bar"
                      style={{
                        width: `${currentTask.progress || 0}%`,
                        backgroundColor: currentTask.status === 'failed' ? '#ff4d4f' : '#1890ff'
                      }}
                    />
                    <span className="progress-text">{Math.round(currentTask.progress || 0)}%</span>
                  </div>
                </div>
                <div className="task-detail">
                  <strong>Status:</strong> <span className="task-message">{currentTask.message}</span>
                </div>
              </div>

              {currentTask.status === 'processing' && (
                <div className="admin-controls" style={{ marginTop: '15px' }}>
                  <button
                    className="admin-btn admin-btn-danger"
                    onClick={() => stopTask(currentTask.task_id)}
                    disabled={currentTask.status === 'stopping'}
                  >
                    {currentTask.status === 'stopping' ? 'Stopping...' : '⏹ Stop Sync'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Active Venues Sync Tasks */}
          {activeTasks.filter(task => task.task_type === 'venues' || task.task_type === 'venues_reconcile').length > 0 && (
            <div className="admin-active-tasks">
              <h3>Active Venues Sync Tasks</h3>
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Task ID</th>
                      <th>Status</th>
                      <th>Progress</th>
                      <th>Started</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeTasks.filter(task => task.task_type === 'venues' || task.task_type === 'venues_reconcile').map((task) => (
                      <tr key={task.task_id}>
                        <td><code>{task.task_id.substring(0, 8)}...</code></td>
                        <td>
                          <span
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(task.status) }}
                          >
                            {task.status?.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <div className="progress-container">
                            <div
                              className="progress-bar small"
                              style={{
                                width: `${task.progress || 0}%`,
                                backgroundColor: task.status === 'failed' ? '#ff4d4f' : '#1890ff'
                              }}
                            />
                            <span className="progress-text">{Math.round(task.progress || 0)}%</span>
                          </div>
                          <div className="task-message-small">{task.message}</div>
                        </td>
                        <td>{formatDate(task.started_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Similarity Engine Tab */}
      {activeTab === 'similarity-engine' && (
        <div className="admin-tab-content">
          <div className="admin-upload-section">
            <h3>Similarity Engine</h3>
            <p className="admin-description">
              Update the similarity search engine indices when new games are added to the database.
              This generates embeddings for missing games and rebuilds the FAISS search indexes,
              then reloads the in-memory engine so new games are immediately searchable.
            </p>

            {/* Stats */}
            {similarityStats && (
              <div className="admin-stats" style={{ marginBottom: '20px' }}>
                <h4 style={{ marginTop: 0, marginBottom: '15px' }}>Current Index Status</h4>
                <div className="stats-grid">
                  <div className="stat-item">
                    <div className="stat-value">{similarityStats.total_games}</div>
                    <div className="stat-label">Total Games</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value">{similarityStats.theme_games}</div>
                    <div className="stat-label">Theme Index</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value">{similarityStats.mechanics_games}</div>
                    <div className="stat-label">Mechanics Index</div>
                  </div>
                </div>

                {/* Missing embeddings warning */}
                {similarityStats.needs_update && (
                  <div className="admin-summary error" style={{ marginTop: '20px' }}>
                    <h4 style={{ marginTop: 0 }}>Missing Embeddings Detected</h4>
                    <div>
                      {similarityStats.missing_theme > 0 && (
                        <div>Theme: {similarityStats.missing_theme} games missing embeddings</div>
                      )}
                      {similarityStats.missing_mechanics > 0 && (
                        <div>Mechanics: {similarityStats.missing_mechanics} games missing embeddings</div>
                      )}
                    </div>
                  </div>
                )}

                {!similarityStats.needs_update && (
                  <div className="admin-summary success" style={{ marginTop: '20px' }}>
                    <h4 style={{ marginTop: 0 }}>All Caught Up</h4>
                    <div>All games have embeddings. No update needed.</div>
                  </div>
                )}
              </div>
            )}

            <div className="admin-file-upload">
              <button
                className="admin-btn admin-btn-primary"
                onClick={handleSimilarityEngineUpdate}
                disabled={uploading}
              >
                {uploading ? 'Updating...' : 'Update Similarity Engine'}
              </button>
            </div>
          </div>

          {/* Current Task Progress */}
          {currentTask && currentTask.task_type === 'similarity' && (
            <div className="admin-progress-section">
              <h3>Similarity Engine Update Progress</h3>
              <div className="admin-task-info">
                <div className="task-detail">
                  <strong>Task ID:</strong> <code>{currentTask.task_id.substring(0, 8)}...</code>
                </div>
                <div className="task-detail">
                  <strong>Status:</strong>
                  <span
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(currentTask.status) }}
                  >
                    {currentTask.status?.toUpperCase()}
                  </span>
                </div>
                <div className="task-detail">
                  <strong>Progress:</strong>
                  <div className="progress-container">
                    <div
                      className="progress-bar"
                      style={{
                        width: `${currentTask.progress || 0}%`,
                        backgroundColor: currentTask.status === 'failed' ? '#ff4d4f' : '#1890ff'
                      }}
                    />
                    <span className="progress-text">{Math.round(currentTask.progress || 0)}%</span>
                  </div>
                </div>
                <div className="task-detail">
                  <strong>Status:</strong> <span className="task-message">{currentTask.message}</span>
                </div>
              </div>

              {currentTask.summary && (
                <div className="admin-summary success">
                  <h4>Similarity Engine Update Completed</h4>
                  <div className="summary-stats">
                    <div>Theme Games: {currentTask.summary.theme_games}</div>
                    <div>Mechanics Games: {currentTask.summary.mechanics_games}</div>
                    <div>Engine Reloaded: {currentTask.summary.reloaded ? 'Yes' : 'No'}</div>
                  </div>
                </div>
              )}

              {currentTask.status === 'processing' && (
                <div className="admin-controls" style={{ marginTop: '15px' }}>
                  <button
                    className="admin-btn admin-btn-danger"
                    onClick={() => stopTask(currentTask.task_id)}
                    disabled={currentTask.status === 'stopping'}
                  >
                    {currentTask.status === 'stopping' ? 'Stopping...' : 'Stop Update'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Active Similarity Engine Tasks */}
          {activeTasks.filter(task => task.task_type === 'similarity').length > 0 && (
            <div className="admin-active-tasks">
              <h3>Active Similarity Engine Tasks</h3>
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Task ID</th>
                      <th>Status</th>
                      <th>Progress</th>
                      <th>Started</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeTasks.filter(task => task.task_type === 'similarity').map((task) => (
                      <tr key={task.task_id}>
                        <td><code>{task.task_id.substring(0, 8)}...</code></td>
                        <td>
                          <span
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(task.status) }}
                          >
                            {task.status?.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <div className="progress-container">
                            <div
                              className="progress-bar small"
                              style={{
                                width: `${task.progress || 0}%`,
                                backgroundColor: task.status === 'failed' ? '#ff4d4f' : '#1890ff'
                              }}
                            />
                            <span className="progress-text">{Math.round(task.progress || 0)}%</span>
                          </div>
                          <div className="task-message-small">{task.message}</div>
                        </td>
                        <td>{formatDate(task.started_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="admin-tab-content">
          <div className="admin-history">
            <div className="admin-controls">
              <button className="admin-btn" onClick={fetchCompletedTasks}>
                🔄 Refresh
              </button>
              <button className="admin-btn" onClick={fetchStats}>
                📊 Stats
              </button>
            </div>

            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Task ID</th>
                    <th>File Name</th>
                    <th>Status</th>
                    <th>Result</th>
                    <th>Completed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {completedTasks.map((task) => (
                    <tr key={task.task_id}>
                      <td><code>{task.task_id.substring(0, 8)}...</code></td>
                      <td>{task.filename || 'N/A'}</td>
                      <td>
                        <span
                          className="status-badge"
                          style={{ backgroundColor: getStatusColor(task.status) }}
                        >
                          {task.status?.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        {task.status === 'completed' && task.summary ? (
                          <div className="task-result">
                            {task.task_type === 'venues' ? (
                              <>
                                <div>Total Venues: {task.summary.total_venues}</div>
                                <div>Active: {task.summary.active_venues}</div>
                                <div>Enriched: {task.summary.enriched_venues}</div>
                                <div>Success Rate: {task.summary.success_rate.toFixed(1)}%</div>
                                {task.summary.venues_by_code && (
                                  <div className="venue-breakdown-small">
                                    {Object.entries(task.summary.venues_by_code).map(([code, stats]) => (
                                      <div key={code}>
                                        <strong>{code.toUpperCase()}:</strong> {stats.total} total, {stats.active} active, {stats.enriched} enriched
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            ) : task.task_type === 'venues_reconcile' ? (
                              <>
                                <div>Processed: {task.summary.processed}</div>
                                <div>Auto-Updated: {task.summary.auto_updated}</div>
                                <div>Pending Deactivations: {task.summary.pending_deactivations}</div>
                              </>
                            ) : (
                              <>
                                <div>Processed: {task.summary.processed}</div>
                                <div>Success: {task.summary.successful}</div>
                                <div>Failed: {task.summary.failed}</div>
                              </>
                            )}
                          </div>
                        ) : task.status === 'failed' ? (
                          <div className="task-result error">{task.error}</div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>{formatDate(task.completed_at)}</td>
                      <td>
                        {task.status === 'processing' || task.status === 'uploading' ? (
                          <button
                            className="admin-btn admin-btn-danger"
                            onClick={() => stopTask(task.task_id)}
                            disabled={task.status === 'stopping'}
                            style={{ marginRight: '8px' }}
                          >
                            {task.status === 'stopping' ? 'Stopping...' : '⏹ Stop'}
                          </button>
                        ) : (
                          <button
                            className="admin-btn admin-btn-danger"
                            onClick={() => deleteTask(task.task_id)}
                          >
                            🗑 Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {completedTasks.length === 0 && (
                <div className="admin-empty-state">
                  No completed updates found.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDataUpdates;
